import { validationHelper } from "code/validationHelper";
import { VALIDATION_CONST } from "constants/ValidationConstants";
import { FleetVehicleStatus } from "enums/FleetEnums";
import { z } from "zod";

const REGO_MAX_LENGTH = 6;
const LENGTH_MIN_M = 3;

const REGO_LABELS = {
	tractorRego: "Registration Truck/Tractor",
	trailer1Rego: "Registration Trailer 1",
	trailer2Rego: "Registration Trailer 2"
} as const;

const regoField = (label: string) =>
	z
		.string()
		.max(REGO_MAX_LENGTH, `${label} must be ${REGO_MAX_LENGTH} characters or less`)
		.regex(/^[A-Z0-9]*$/, `${label} must be alphanumeric uppercase`);

export const UpsertVehicleSchema = buildUpsertVehicleSchema(null);

export function buildUpsertVehicleSchema(maxLength: number | null) {
	const lengthM = z
		.string({ error: validationHelper })
		.min(1, "Length is required")
		.refine((val) => !Number.isNaN(Number(val.trim())), "Length must be a valid number")
		.refine((val) => /^\d+(\.\d)?$/.test(val.trim()), "Length must have at most 1 decimal place")
		.refine(
			(val) => Number(val.trim()) >= LENGTH_MIN_M,
			`Length must be at least ${LENGTH_MIN_M}m`
		);

	return z
		.object({
			registration: z
				.string({ error: validationHelper })
				.min(1, "Fleet number is required")
				.max(
					VALIDATION_CONST.NVARCHAR_50,
					`Fleet number must be ${VALIDATION_CONST.NVARCHAR_50} characters or less`
				),
			type: z
				.string({ error: validationHelper })
				.min(1, "Type is required")
				.max(
					VALIDATION_CONST.NVARCHAR_100,
					`Type must be ${VALIDATION_CONST.NVARCHAR_100} characters or less`
				),
			lengthM:
				maxLength != null
					? lengthM.refine(
							(val) => Number(val.trim()) <= maxLength,
							`Length must be at most ${maxLength}m`
						)
					: lengthM,
			refrigerated: z
				.string({ error: validationHelper })
				.refine((val) => ["yes", "no"].includes(val), "Refrigerated must be yes or no"),
			driverContactId: z.number().int().positive().nullable().optional(),
			tractorRego: regoField(REGO_LABELS.tractorRego).optional(),
			trailer1Rego: regoField(REGO_LABELS.trailer1Rego).optional(),
			trailer2Rego: regoField(REGO_LABELS.trailer2Rego).optional(),
			status: z
				.string({ error: validationHelper })
				.min(1, "Status is required")
				.refine(
					(val) => Object.values(FleetVehicleStatus).includes(val as FleetVehicleStatus),
					"Status must be Active or Deactivated"
				)
		})
		.superRefine((data, ctx) => {
			const pairs = [
				["tractorRego", "trailer1Rego"],
				["tractorRego", "trailer2Rego"],
				["trailer1Rego", "trailer2Rego"]
			] as const;

			for (const [a, b] of pairs) {
				if (data[a] && data[b] && data[a] === data[b]) {
					const message = `${REGO_LABELS[a]} and ${REGO_LABELS[b]} must be different`;
					ctx.addIssue({ code: "custom", message, path: [a] });
					ctx.addIssue({ code: "custom", message, path: [b] });
				}
			}
		});
}
